import { SOCKET_NAME } from '@act/shared/definitions';
import {
  HealthCheckPayload,
  HealthCheckTypes,
} from '@act/shared/socket-events';
import { AppDispatch, getStore } from 'redux/reemaStore.util';
import {
  ManagerOptions,
  Socket as SocketIO,
  SocketOptions,
  io,
} from 'socket.io-client';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';
import { getToken } from '../utils/firebaseAuth.util';

// Import types only to avoid circular dependency
import type { SocketState } from '../slices/websocketSlice.slice';

export class HealthCheck {
  readonly type = HealthCheckTypes.HEALTH_CHECK;

  constructor(public payload: HealthCheckPayload) {}
}

const listenerDefaults = {
  baseSocket: [],
};

export type SocketChannelTypes = 'baseSocket';

// Helper to get websocket state without importing the slice
const getWebsocketState = (state): { deviceId: string; baseSocket: SocketState } => {
  return (state as { websocket: { deviceId: string; baseSocket: SocketState } })?.websocket;
};

class ActSocket {
  constructor(protected baseSocket: SocketIO) {}

  private listenEvents = listenerDefaults;

  private healthCheckInterval = 20000;
  private healthCheckProcesses = {
    baseSocket: null,
  };

  async connect() {
    const token = await getToken();
    const websocketState = getWebsocketState(getStore().getState());
    this.disconnect();

    const impersonatedUserId = localStorage.getItem('impersonatedUserId');

    const connectionArgs: Partial<ManagerOptions & SocketOptions> = {
      withCredentials: true,
      path: '/socket.io',
      autoConnect: false,
      transports: ['websocket'],
      query: {
        deviceId: websocketState.deviceId,
      },
      auth: {
        token,
        impersonatedUserId,
      },
    };

    this.baseSocket = io(
      `${getEnvironment().serviceUris.websocketService}/base`,
      connectionArgs
    );
    this.baseSocket.connect();
    this.registerEventListeners('baseSocket');
  }

  registerEventListeners(socketType: SocketChannelTypes) {
    const socket = this[socketType];
    const dispatch: AppDispatch = getStore().dispatch;
    socket
      .on('connect', () => {
        // Dispatch action by type string to avoid circular dependency
        dispatch({ type: 'websocket/connectionEstablished', payload: socketType });
        if (this.healthCheckProcesses[socketType]) {
          clearInterval(this.healthCheckProcesses[socketType]);
        }
        this.healthCheckProcesses[socketType] = setInterval(
          this.healthCheck.bind(this, socketType),
          this.healthCheckInterval
        );
      })
      .on('disconnect', () => {
        // Dispatch action by type string to avoid circular dependency
        dispatch({ type: 'websocket/connectionLost', payload: socketType });
        this.cleanUp(socketType);
      })
      .on('reconnect', () => {
        // Dispatch action by type string to avoid circular dependency
        dispatch({ type: 'websocket/connectionEstablished', payload: socketType });
      })
      .onAny((eventName, payload) =>
        this.catchListener(eventName, payload, socketType)
      );
  }

  disconnect() {
    const socket = this['baseSocket'];
    if (socket) {
      // Only close if the socket is actually connected
      if (socket.connected) {
        socket.close();
      }
      this.cleanUp('baseSocket');
      this['baseSocket'] = null;
    }
  }

  cleanUp(socketType) {
    const socket = this[socketType];
    if (!socket) return;
    for (const eventName of this.listenEvents[socketType]) {
      socket.off(eventName);
    }
    this.listenEvents = listenerDefaults;
    if (this.healthCheckProcesses[socketType]) {
      clearInterval(this.healthCheckProcesses[socketType]);
    }
  }

  registerListener(event, callback, socketType: SocketChannelTypes) {
    const socket = this[socketType];
    this.listenEvents[socketType].push(event);
    socket.on(event, callback);
  }

  send(message, socketChannel: SocketChannelTypes) {
    const socket = this[socketChannel];
    if (socket) {
      socket.send(JSON.stringify(message));
    } else {
      console.log('Cannot send message, channel not connected.');
    }
  }

  async healthCheck(socketType) {
    const websocketState = getWebsocketState(getStore().getState());
    const socketState: SocketState = websocketState[socketType];
    const socket = this[socketType];
    if (!socket) return;
    const token = await getToken();
    socket.auth = { token };

    const message: HealthCheckPayload = {
      deviceId: websocketState.deviceId,
      socketId: socketState.initializedId,
      socketName: socketState.socketName as SOCKET_NAME,
      token,
    };

    socket.emit(HealthCheckTypes.HEALTH_CHECK, message);
  }

  // Catch websocket events that we aren't listening to.
  catchListener(eventName, payload, socketType) {
    if (this.listenEvents[socketType].includes(eventName)) return;
    if (!getEnvironment().production) {
      console.log(`Unhandled ${socketType} message: ${eventName}`, payload);
    }
  }
}

export const actSocket = new ActSocket(null);
